import axios from "axios";
import React, { useMemo, useState } from "react";
import { DatePicker } from "antd";
import { Badge, Button, Card, Modal } from "react-bootstrap";
import moment from "moment";
import AccompanyingPersonCard, {
  AccompanyingPersonModal,
  validateAccompanyingPersonPayload,
} from "./AccompanyingPerson";
import TicketOptionCard from "./TicketOptionCard";

const formatMoney = (cents, currency) => {
  if (cents === null || cents === undefined) return "-";
  if (Number(cents) === 0) return "-";

  const ccy = (currency || "myr").toString().toUpperCase();
  const amount = Number(cents) / 100;

  return `${ccy} ${amount.toFixed(2)}`;
};

const formatDateLabel = (isoDate) => {
  if (!isoDate) return "-";
  try {
    return new Date(`${isoDate}T00:00:00`).toLocaleDateString();
  } catch (e) {
    return isoDate;
  }
};

const formatTimeLabel = (isoDateTime) => {
  if (!isoDateTime) return "-";
  try {
    return new Date(isoDateTime).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return isoDateTime;
  }
};

const nightsBetween = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return 0;
  const start = new Date(`${checkIn}T00:00:00`);
  const end = new Date(`${checkOut}T00:00:00`);
  const diff = end.getTime() - start.getTime();
  if (Number.isNaN(diff)) return 0;
  return Math.max(0, Math.round(diff / (1000 * 60 * 60 * 24)));
};

const getRemainingCapacity = (roomType) => {
  if (!roomType) return null;
  const remaining = roomType.remainingCapacity;
  if (remaining === null || remaining === undefined) return null;
  const value = Number(remaining);
  return Number.isNaN(value) ? null : value;
};

const isRoomTypeSoldOut = (roomType) => {
  if (!roomType) return false;
  if (roomType.soldOut === true) return true;
  const remaining = getRemainingCapacity(roomType);
  if (remaining === null) return false;
  return remaining <= 0;
};

const TicketSelection = ({
  token,
  createOrderUrl,
  currency,
  user,
  access,
  accompanying,
  registrationOptions,
  products,
  workshops,
  hotels,
  reservationDateRange,
}) => {
  const { RangePicker } = DatePicker;

  const [cart, setCart] = useState({
    physicalProductId: null,
    workshopAmProductId: null,
    workshopPmProductId: null,
    receptionProductId: null,
    galaProductId: null,
    hotelReservation: null, // { hotelId, roomTypeId, checkIn, checkOut }
  });
  const [accompanyingPersons, setAccompanyingPersons] = useState([]);

  const [hotelModalOpen, setHotelModalOpen] = useState(false);
  const [workshopModalOpen, setWorkshopModalOpen] = useState(false);
  const [accompanyingModalOpen, setAccompanyingModalOpen] = useState(false);
  const [editingAccompanyingPerson, setEditingAccompanyingPerson] =
    useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const GRANT_MEMBER_TYPES = ["malaysian_master_trainee", "malaysian_officer"];
  const showGrantCard = GRANT_MEMBER_TYPES.includes(user?.memberType);

  const isExhibitor = Boolean(user?.isExhibitor);
  const grantCouponCode = "AMASMT0001"; // TODO: replace once the coupon code is confirmed

  const hasPhysicalAccess = Boolean(access?.physical);
  const hasPhysicalInCart = Boolean(cart.physicalProductId);
  const hasHotelInCart = Boolean(cart.hotelReservation);

  const canAddPhysical =
    !hasPhysicalAccess && !hasPhysicalInCart && !hasHotelInCart;
  const canAddPhysicalWithHotel =
    !hasPhysicalAccess &&
    !hasPhysicalInCart &&
    !hasHotelInCart &&
    !access?.hotel;
  const canAddAddOns = hasPhysicalAccess || hasPhysicalInCart || hasHotelInCart;
  // bypassed users may buy workshops on their own, without a 3-Day Congress Pass
  const workshopRequiresPhysical = access?.workshopRequiresPhysical !== false;
  const canAddWorkshops = canAddAddOns || !workshopRequiresPhysical;

  const physicalProduct = products?.physical;
  const hotelProduct = products?.hotel;
  const hotelProductHidden = !hotelProduct;
  const hotelProductSoldOut = Boolean(hotelProduct?.soldOut);
  const hotelProductAvailable = !hotelProductHidden && !hotelProductSoldOut;
  const receptionProduct = products?.reception;
  const galaProduct = products?.gala;
  const maxAccompanying = accompanying?.maxCount || 3;
  const existingAccompanying = accompanying?.existingCount || 0;
  const accompanyingEvents = accompanying?.events || [];
  const accompanyingEventsByKey = useMemo(() => {
    const map = {};
    accompanyingEvents.forEach((event) => {
      if (!event?.name) return;
      if (event.name === "accompanying_opening") map.opening = event;
      if (event.name === "accompanying_reception") map.reception = event;
      if (event.name === "accompanying_gala") map.gala = event;
      if (event.name === "accompanying_ticket") map.ticket = event;
    });
    return map;
  }, [accompanyingEvents]);
  const hasAccompanyingEvents = accompanyingEvents.length > 0;

  const cartItems = useMemo(() => {
    const items = [];

    const hotelInCart = Boolean(cart.hotelReservation) && hotelProductAvailable

    if (cart.physicalProductId && !hotelInCart && physicalProduct) {
      items.push({
        key: "physical",
        title: physicalProduct.title,
        subtitle: `${physicalProduct.buyerTypeLabel} • ${physicalProduct.seasonTypeLabel}`,
        amountCents: physicalProduct.priceCents,
      });
    }

    if (hotelInCart && physicalProduct) {
      const hr = cart.hotelReservation;
      const selectedHotel = (hotels || []).find((h) => h.id === hr.hotelId);
      const selectedRoomType = (selectedHotel?.roomTypes || []).find(
        (rt) => rt.id === hr.roomTypeId,
      );
      const nights = nightsBetween(hr.checkIn, hr.checkOut);
      const pricePerNightCents =
        currency === "usd"
          ? selectedRoomType?.priceUsdCents
          : selectedRoomType?.priceMyrCents;
      const hotelTotalCents = (pricePerNightCents || 0) * nights;
      const amountCents = physicalProduct.priceCents + hotelTotalCents;

      items.push({
        key: "physical_hotel",
        title:
          "3-Day Congress Pass | 28th – 30th August 2026 (Friday-Sunday) + Hotel",
        subtitle: [
          selectedHotel?.name,
          selectedRoomType?.name,
          `${formatDateLabel(hr.checkIn)} → ${formatDateLabel(hr.checkOut)}`,
          `${nights} night${nights === 1 ? "" : "s"}`,
        ]
          .filter(Boolean)
          .join(" • "),
        amountCents,
      });
    }

    const wsAm = (workshops?.am || []).find(
      (p) => p.id === cart.workshopAmProductId,
    );
    if (wsAm) {
      console.log("wsAm", wsAm);
      items.push({
        key: "workshop_am",
        title: wsAm.title,
        subtitle: `Morning: ${formatTimeLabel(wsAm.startAt)} - ${formatTimeLabel(wsAm.endAt)}`,
        amountCents: wsAm.priceCents,
      });
    }

    const wsPm = (workshops?.pm || []).find(
      (p) => p.id === cart.workshopPmProductId,
    );
    if (wsPm) {
      console.log("wsPm", wsPm);
      items.push({
        key: "workshop_pm",
        title: wsPm.title,
        subtitle: `Afternoon: ${formatTimeLabel(wsPm.startAt)} - ${formatTimeLabel(wsPm.endAt)}`,
        amountCents: wsPm.priceCents,
      });
    }

    if (cart.receptionProductId && receptionProduct) {
      items.push({
        key: "reception",
        title: receptionProduct.title,
        subtitle: "07:00 PM Onwards",
        amountCents: receptionProduct.priceCents,
      });
    }

    if (cart.galaProductId && galaProduct) {
      items.push({
        key: "gala",
        title: galaProduct.title,
        subtitle: "07:30 PM Onwards",
        amountCents: galaProduct.priceCents,
      });
    }

    if (accompanyingPersons.length > 0) {
      accompanyingPersons.forEach((person, index) => {
        const displayName =
          person?.user?.full_name ||
          person?.user?.email ||
          `Accompanying Person ${index + 1}`;
        const events = person?.events || {};
        const groupKey = `accompanying-${person.id}`;

        if (events.ticket) {
          const event = accompanyingEventsByKey.ticket;
          if (event) {
            items.push({
              key: `accompanying-${person.id}-ticket`,
              groupKey,
              groupLabel: displayName,
              title: event.title || "Accompanying Person - Ticket",
              amountCents: Number(event.priceCents || 0),
            });
          }
        }
        if (events.opening) {
          const event = accompanyingEventsByKey.opening;
          if (event) {
            items.push({
              key: `accompanying-${person.id}-opening`,
              groupKey,
              groupLabel: displayName,
              title: event.title || "Accompanying Person - Opening Ceremony",
              amountCents: Number(event.priceCents || 0),
            });
          }
        }
        if (events.reception) {
          const event = accompanyingEventsByKey.reception;
          if (event) {
            items.push({
              key: `accompanying-${person.id}-reception`,
              groupKey,
              groupLabel: displayName,
              title: event.title || "Accompanying Person - Welcome Reception",
              amountCents: Number(event.priceCents || 0),
            });
          }
        }
        if (events.gala) {
          const event = accompanyingEventsByKey.gala;
          if (event) {
            items.push({
              key: `accompanying-${person.id}-gala`,
              groupKey,
              groupLabel: displayName,
              title:
                event.title || "Accompanying Person - Congress Gala Dinner",
              amountCents: Number(event.priceCents || 0),
            });
          }
        }
      });
    }

    return items;
  }, [
    cart,
    physicalProduct,
    hotelProductAvailable,
    receptionProduct,
    galaProduct,
    workshops,
    hotels,
    currency,
    accompanyingPersons,
    accompanyingEventsByKey,
  ]);

  const cartTotalCents = useMemo(() => {
    return cartItems.reduce((sum, item) => sum + (item.amountCents || 0), 0);
  }, [cartItems]);

  const { regularItems, accompanyingGroups } = useMemo(() => {
    const regular = [];
    const groups = {};

    cartItems.forEach((item) => {
      if (item.groupKey && item.groupLabel) {
        if (!groups[item.groupKey]) {
          groups[item.groupKey] = {
            key: item.groupKey,
            label: item.groupLabel,
            items: [],
          };
        }
        groups[item.groupKey].items.push(item);
        return;
      }
      regular.push(item);
    });

    return { regularItems: regular, accompanyingGroups: Object.values(groups) };
  }, [cartItems]);

  const removeFromCart = (key) => {
    setCart((prev) => {
      const next = { ...prev };

      if (key === "physical" || key === "physical_hotel") {
        next.physicalProductId = null;
        next.hotelReservation = null;

        if (!hasPhysicalAccess) {
          if (workshopRequiresPhysical) {
            next.workshopAmProductId = null;
            next.workshopPmProductId = null;
          }
          next.receptionProductId = null;
          next.galaProductId = null;
        }
      }

      if (key === "workshop_am") next.workshopAmProductId = null;
      if (key === "workshop_pm") next.workshopPmProductId = null;
      if (key === "reception") next.receptionProductId = null;
      if (key === "gala") next.galaProductId = null;

      return next;
    });
  };

  const saveAccompanyingPerson = (person) => {
    setAccompanyingPersons((prev) => {
      const index = prev.findIndex((item) => item.id === person.id);
      if (index === -1) return [...prev, person];
      const next = [...prev];
      next[index] = person;
      return next;
    });
    setEditingAccompanyingPerson(null);
  };

  const removeAccompanyingPerson = (id) => {
    setAccompanyingPersons((prev) => prev.filter((person) => person.id !== id));
  };

  const openAccompanyingModal = (person = null) => {
    setEditingAccompanyingPerson(person);
    setAccompanyingModalOpen(true);
  };

  const closeAccompanyingModal = () => {
    setAccompanyingModalOpen(false);
    setEditingAccompanyingPerson(null);
  };

  const validateAccompanyingPersons = () => {
    for (let index = 0; index < accompanyingPersons.length; index += 1) {
      const person = accompanyingPersons[index];
      const errors = validateAccompanyingPersonPayload(person);
      const messages = Object.values(errors);
      if (messages.length > 0) {
        const name =
          person?.user?.full_name ||
          person?.user?.email ||
          `Accompanying Person ${index + 1}`;
        return `Accompanying Person (${name}): ${messages.join(" ")}`;
      }
    }
    return null;
  };

  const createOrder = async () => {
    setSubmitError(null);
    setSubmitting(true);

    try {
      const accompanyingValidation = validateAccompanyingPersons();
      if (accompanyingValidation) {
        setSubmitError(accompanyingValidation);
        setSubmitting(false);
        return;
      }

      const productIds = [];

      if (cart.physicalProductId) productIds.push(cart.physicalProductId);
      if (cart.workshopAmProductId) productIds.push(cart.workshopAmProductId);
      if (cart.workshopPmProductId) productIds.push(cart.workshopPmProductId);
      if (cart.receptionProductId) productIds.push(cart.receptionProductId);
      if (cart.galaProductId) productIds.push(cart.galaProductId);

      const hotelReservation =
        hotelProductAvailable && cart.hotelReservation?.roomTypeId ? cart.hotelReservation : null;
      if (hotelReservation) {
        productIds.push(hotelProduct.id);
      }

      if (productIds.length === 0 && accompanyingPersons.length === 0) {
        setSubmitError("Your cart is empty.");
        setSubmitting(false);
        return;
      }

      const payload = {
        product_ids: productIds,
        hotel_reservation: hotelReservation
          ? {
              room_type_id: hotelReservation.roomTypeId,
              check_in: hotelReservation.checkIn,
              check_out: hotelReservation.checkOut,
            }
          : null,
      };

      if (accompanyingPersons.length > 0) {
        payload.accompanying_persons = accompanyingPersons.map((person) => ({
          user: person.user,
          events: person.events,
        }));
      }

      const response = await axios.post(
        createOrderUrl,
        { cart: payload },
        {
          headers: {
            "X-CSRF-Token": token,
            "X-Requested-With": "XMLHttpRequest",
            Accept: "application/json",
          },
        },
      );

      const redirectUrl = response?.data?.redirect_url;
      if (!redirectUrl) {
        throw new Error("Missing redirect URL");
      }

      window.location.assign(redirectUrl);
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.message ||
        "Unable to continue. Please try again.";
      setSubmitError(msg);
      setSubmitting(false);
    }
  };

  const PhysicalCard = () => {
    const soldOut = physicalProduct?.soldOut;
    const inCart = Boolean(cart.physicalProductId && !cart.hotelReservation);
    const disabled = !inCart && (soldOut || !canAddPhysical);
    const priceLabel = physicalProduct
      ? formatMoney(physicalProduct.priceCents, physicalProduct.currency)
      : "-";

    return (
      <TicketOptionCard
        title={physicalProduct?.title || "3-Day Congress Pass"}
        badges={[
          physicalProduct?.buyerTypeLabel,
          physicalProduct?.seasonTypeLabel,
        ].filter(Boolean)}
        description={
          physicalProduct?.description ? (
            <span
              dangerouslySetInnerHTML={{
                __html: (physicalProduct?.description || "-").replace(
                  /\n/g,
                  "<br>",
                ),
              }}
            />
          ) : null
        }
        priceLabel={priceLabel}
        priceClassName={soldOut ? "text-red-500" : ""}
        soldOut={soldOut}
        isInCart={inCart}
        actionDisabled={disabled}
        onAction={() => {
          if (inCart) {
            removeFromCart("physical");
            return;
          }
          setCart((prev) => ({
            ...prev,
            physicalProductId: physicalProduct?.id || null,
          }));
        }}
      />
    );
  };

  const PhysicalWithHotelCard = () => {
    if (hotelProductHidden) return null;

    const soldOut = Boolean(physicalProduct?.soldOut) || hotelProductSoldOut;
    const inCart = Boolean(cart.hotelReservation);
    const disabled = !inCart && (soldOut || !canAddPhysicalWithHotel);

    return (
      <TicketOptionCard
        title="3-Day Congress Pass | 28th – 30th August 2026 (Friday-Sunday) + Hotel"
        badges={[
          user?.memberTypeLabel,
          physicalProduct?.seasonTypeLabel,
        ].filter(Boolean)}
        description="Includes 3-day congress access and hotel accommodation."
        priceLabel={
          physicalProduct
            ? `${formatMoney(physicalProduct.priceCents, physicalProduct.currency)} + Hotel`
            : "-"
        }
        soldOut={soldOut}
        isInCart={inCart}
        actionDisabled={disabled}
        onAction={() => {
          if (inCart) {
            removeFromCart("physical_hotel");
            return;
          }
          setHotelModalOpen(true);
        }}
      />
    );
  };

  const WorkshopCard = () => {
    const workshopProducts = [
      ...(workshops?.am || []),
      ...(workshops?.pm || []),
    ];
    const soldOut =
      workshopProducts.length > 0 &&
      workshopProducts.every((p) => {
        if (p?.soldOut) return true;
        const remaining = p?.remainingSeats;
        return remaining !== null && remaining !== undefined && remaining <= 0;
      });

    const inCart = Boolean(
      cart.workshopAmProductId || cart.workshopPmProductId,
    );
    const disabled =
      workshopProducts.length <= 0 ||
      (!inCart &&
        (soldOut ||
          !canAddWorkshops ||
          (access?.workshopAm && access?.workshopPm)));
    // get cheapest workshop out of the two sessions for price display
    const cheapestWorkshop = workshopProducts.reduce((cheapest, p) => {
      if (!p) return cheapest;
      if (!cheapest) return p;
      const cheapestPrice = cheapest.priceCents || Infinity;
      const currentPrice = p.priceCents || Infinity;
      return currentPrice < cheapestPrice ? p : cheapest;
    }, null);

    return (
      <TicketOptionCard
        title="Pre Congress Workshops | 27th August 2026 (Thursday)"
        // badges={[user?.memberTypeLabel, "All Seasons"].filter(Boolean)}
        // priceLabel={cheapestWorkshop ? `From: ${formatMoney(cheapestWorkshop.priceCents, cheapestWorkshop.currency)}` : "Free"}
        soldOut={soldOut}
        isInCart={inCart}
        actionDisabled={disabled}
        onAction={() => {
          if (inCart) {
            setCart((prev) => ({
              ...prev,
              workshopAmProductId: null,
              workshopPmProductId: null,
            }));
            return;
          }
          setWorkshopModalOpen(true);
        }}
      />
    );
  };

  const FreeAddOnCard = ({ title, product, accessFlag, cartKey, disabled }) => {
    const soldOut = Boolean(product?.soldOut);
    const alreadyHas = Boolean(access?.[accessFlag]);
    const inCart = Boolean(cart[cartKey]);
    const disable_conditions =
      alreadyHas || (!inCart && (soldOut || !canAddAddOns));

    return (
      <TicketOptionCard
        title={title}
        // badges={[
        //   product?.buyerTypeLabel || user?.memberTypeLabel,
        //   product?.seasonTypeLabel || "All Seasons",
        // ].filter(Boolean)}
        // priceLabel="FOC"
        soldOut={soldOut}
        isInCart={inCart}
        actionDisabled={disabled || disable_conditions}
        onAction={() => {
          setCart((prev) => ({
            ...prev,
            [cartKey]: inCart ? null : product?.id || null,
          }));
        }}
      />
    );
  };

  const HotelModal = () => {
    const defaultCheckIn = reservationDateRange?.start || "";
    const initialHotelId =
      cart.hotelReservation?.hotelId || hotels?.[0]?.id || null;
    const [hotelId, setHotelId] = useState(initialHotelId);
    const hotel = (hotels || []).find((h) => h.id === hotelId) || null;

    const firstAvailableRoomTypeId = (roomTypes = []) => {
      const available = roomTypes.find((rt) => !isRoomTypeSoldOut(rt));
      return available?.id || roomTypes[0]?.id || null;
    };

    const initialRoomTypeId = (() => {
      const selectedId = cart.hotelReservation?.roomTypeId;
      if (selectedId) {
        const selectedRoom = (hotel?.roomTypes || []).find(
          (rt) => rt.id === selectedId,
        );
        if (selectedRoom && !isRoomTypeSoldOut(selectedRoom)) return selectedId;
      }
      return firstAvailableRoomTypeId(hotel?.roomTypes);
    })();
    const [roomTypeId, setRoomTypeId] = useState(initialRoomTypeId);

    const [checkIn, setCheckIn] = useState(
      cart.hotelReservation?.checkIn || defaultCheckIn,
    );
    const [checkOut, setCheckOut] = useState(
      cart.hotelReservation?.checkOut || "", // force explicit selection
    );

    const [error, setError] = useState(null);
    const reservationStart = reservationDateRange?.start
      ? moment(reservationDateRange.start, "YYYY-MM-DD")
      : null;
    const reservationEnd = reservationDateRange?.end
      ? moment(reservationDateRange.end, "YYYY-MM-DD")
      : null;

    const selectedRoomType = (hotel?.roomTypes || []).find(
      (rt) => rt.id === roomTypeId,
    );
    const selectedRoomSoldOut = isRoomTypeSoldOut(selectedRoomType);
    const allRoomTypesSoldOut =
      (hotel?.roomTypes || []).length > 0 &&
      (hotel?.roomTypes || []).every((rt) => isRoomTypeSoldOut(rt));
    const nights = nightsBetween(checkIn, checkOut);
    const pricePerNightCents =
      currency === "usd"
        ? selectedRoomType?.priceUsdCents
        : selectedRoomType?.priceMyrCents;
    const hotelTotalCents = (pricePerNightCents || 0) * nights;
    const hotelTotalLabel = `${(currency || "myr")
      .toString()
      .toUpperCase()} ${((hotelTotalCents || 0) / 100).toFixed(2)}`;
    const subtotalCents = (physicalProduct?.priceCents || 0) + hotelTotalCents;

    const onAdd = () => {
      setError(null);

      if (!hotelId || !roomTypeId) {
        setError("Please select a hotel and room type.");
        return;
      }
      if (selectedRoomSoldOut) {
        setError("Selected room type is sold out.");
        return;
      }
      if (!checkIn || !checkOut) {
        setError("Please select your check-in and check-out dates.");
        return;
      }
      if (nights <= 0) {
        setError("Check-out date must be after check-in date.");
        return;
      }

      setCart((prev) => ({
        ...prev,
        physicalProductId: physicalProduct?.id || null,
        hotelReservation: {
          hotelId,
          roomTypeId,
          checkIn,
          checkOut,
        },
      }));
      setHotelModalOpen(false);
    };

    const disabledReservationDate = (current) => {
      if (!current) return false;
      const day = current.clone().startOf("day");
      if (reservationStart && day.isBefore(reservationStart, "day"))
        return true;
      if (reservationEnd && day.isAfter(reservationEnd, "day")) return true;
      return false;
    };

    return (
      <Modal
        show={hotelModalOpen}
        onHide={() => setHotelModalOpen(false)}
        size="lg"
        centered
        className="ticket-modal"
      >
        <Modal.Body style={{ background: "#ebebeb" }}>
          <div className="flex flex-col gap-4 text-sm md:p-10">
            <div>
              <h3>Select Hotel</h3>
              <p className="m-0">
                3-Day Congress Pass is compulsory in this package. Please select
                your hotel, room type, and staying nights.
              </p>
            </div>
            {error && <div className="alert alert-danger">{error}</div>}

            <div className="p-3 bg-white rounded md:py-4 md:px-6">
              <div className="gap-3 d-flex justify-content-between align-items-center">
                <div>{physicalProduct?.title || "3-Day Congress Pass"}*</div>
                <div className="fw-bold text-nowrap">
                  {formatMoney(
                    physicalProduct?.priceCents,
                    physicalProduct?.currency || currency,
                  )}
                </div>
              </div>
            </div>

            <div className="p-3 bg-white rounded md:py-4 md:px-6">
              <div>Hotel</div>
              <div className="small text-muted">Please select a hotel.</div>
              <div className="small text-muted">
                <ul className="pl-5">
                  <li>Check-in Time: from 4:00 PM.</li>
                  <li>Check-out Time: before 12:00 PM.</li>
                  <li>Room comes with breakfast for up to 2 persons.</li>
                </ul>
              </div>
              <div className="flex flex-col gap-2 mt-2">
                {(hotels || []).map((h) => {
                  const checked = h.id === hotelId;
                  return (
                    <label
                      key={h.id}
                      className="flex items-center gap-3 p-2 border rounded cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="hotelId"
                        checked={checked}
                        onChange={() => {
                          setHotelId(h.id);
                          setRoomTypeId(firstAvailableRoomTypeId(h.roomTypes));
                        }}
                        className="flex-shrink-0 m-0"
                        style={{
                          width: "1.25rem",
                          height: "1.25rem",
                          boxShadow: "none",
                        }}
                      />
                      <div>
                        <div>{h.name}</div>
                        <div className="flex flex-col gap-1 md:flex-row">
                          {h.starRating && (
                            <div className="p-1 text-xs text-yellow-300 w-fit">
                              {Array.from({ length: h.starRating }).map(
                                (_, i) => (
                                  <span key={i}>★</span>
                                ),
                              )}
                            </div>
                          )}
                          {h.description && (
                            <div className="p-1 text-xs text-muted w-fit">
                              {h.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="p-3 bg-white rounded md:py-4 md:px-6">
              <div>Room Type</div>
              <div className="small text-muted">Please select a room type.</div>
              {allRoomTypesSoldOut && (
                <div className="mt-1 text-red-500 small">
                  All room types are sold out for this hotel.
                </div>
              )}
              <div className="flex flex-col gap-2 mt-2">
                {(hotel?.roomTypes || []).map((rt) => {
                  const rtPriceCents =
                    currency === "usd" ? rt.priceUsdCents : rt.priceMyrCents;
                  const remaining = getRemainingCapacity(rt);
                  const soldOut = isRoomTypeSoldOut(rt);
                  const checked = rt.id === roomTypeId;
                  const showAvailability = remaining !== null || soldOut;
                  return (
                    <label
                      key={rt.id}
                      className={`flex items-center gap-3 p-2 border rounded ${
                        soldOut
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      }`}
                    >
                      <input
                        type="radio"
                        name="roomTypeId"
                        checked={checked}
                        disabled={soldOut}
                        onChange={() => setRoomTypeId(rt.id)}
                        className="flex-shrink-0 m-0"
                        style={{
                          width: "1.25rem",
                          height: "1.25rem",
                          boxShadow: "none",
                        }}
                      />
                      <div>
                        <div>{rt.name}</div>
                        <div className="small text-muted">
                          {formatMoney(rtPriceCents, currency)} / night
                          {showAvailability && (
                            <>
                              <span className="mx-2">|</span>
                              {!soldOut && remaining !== null ? (
                                <span className="text-success">Available</span>
                              ) : (
                                <span className="text-red-500">Sold Out</span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      {rt.imageUrl && (
                        <img
                          src={rt.imageUrl}
                          alt={rt.name || "Room type"}
                          className="flex-shrink-0 ml-auto rounded"
                          style={{
                            width: "72px",
                            height: "72px",
                            objectFit: "cover",
                          }}
                        />
                      )}
                    </label>
                  );
                })}
                {(hotel?.roomTypes || []).length === 0 && (
                  <div className="small text-muted">
                    No room types available.
                  </div>
                )}
              </div>
            </div>

            <div className="p-3 bg-white rounded md:py-4 md:px-6">
              <div>Check-in & Check-out Date</div>
              <div className="flex flex-col gap-3 mt-2 md:flex-row md:justify-between md:items-center">
                <RangePicker
                  style={{
                    height: "40px",
                    width: "450px",
                    maxWidth: "100%",
                    borderRadius: "12px",
                  }}
                  getPopupContainer={(triggerNode) =>
                    triggerNode?.closest(".modal-content") || document.body
                  }
                  popupStyle={{ zIndex: 2000 }}
                  allowEmpty={[false, true]}
                  placeholder={["Check-in Date", "Check-out Date"]}
                  separator="→"
                  value={[
                    checkIn
                      ? moment(checkIn, "YYYY-MM-DD")
                      : moment(defaultCheckIn, "YYYY-MM-DD"),
                    checkOut ? moment(checkOut, "YYYY-MM-DD") : null,
                  ]}
                  format="YYYY-MM-DD"
                  disabledDate={disabledReservationDate}
                  onChange={(_, dateStrings) => {
                    setCheckIn(dateStrings?.[0] || defaultCheckIn);
                    setCheckOut(dateStrings?.[1] || "");
                  }}
                />
                <div className="fw-bold">
                  {hotelTotalLabel} / {nights} night{nights === 1 ? "" : "s"}
                </div>
              </div>
            </div>

            <div className="flex justify-center gap-2">
              <Button
                className="theme-btn-light"
                onClick={() => setHotelModalOpen(false)}
              >
                Close
              </Button>
              <Button
                className="theme-btn"
                onClick={onAdd}
                disabled={selectedRoomSoldOut || allRoomTypesSoldOut}
              >
                Done
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    );
  };

  const WorkshopModal = () => {
    const [selectedAm, setSelectedAm] = useState(cart.workshopAmProductId);
    const [selectedPm, setSelectedPm] = useState(cart.workshopPmProductId);
    const [error, setError] = useState(null);

    const add = () => {
      setError(null);

      if (!selectedAm && !selectedPm) {
        setError("Please select at least one workshop.");
        return;
      }

      setCart((prev) => ({
        ...prev,
        workshopAmProductId: selectedAm,
        workshopPmProductId: selectedPm,
      }));
      setWorkshopModalOpen(false);
    };

    const renderWorkshopOption = (p, selected, setSelected) => {
      const remaining = p.remainingSeats;
      const soldOut =
        remaining == null || remaining == undefined || remaining <= 0;
      const disabled = soldOut;
      const timeLabel =
        p.startAt && p.endAt
          ? `${formatTimeLabel(p.startAt)} - ${formatTimeLabel(p.endAt)}`
          : null;
      // const timeLabel =
      //   p.startAt && p.endAt
      //     ? `${new Date(p.startAt).toLocaleTimeString([], {
      //         hour: "2-digit",
      //         minute: "2-digit",
      //       })} - ${new Date(p.endAt).toLocaleTimeString([], {
      //         hour: "2-digit",
      //         minute: "2-digit",
      //       })}`
      //     : null;

      return (
        <label
          key={p.id}
          htmlFor={`ws-${p.id}`}
          className="flex items-center gap-3 p-2 border rounded cursor-pointer"
        >
          <input
            type="radio"
            name={`ws-${p.name}`}
            id={`ws-${p.id}`}
            disabled={disabled}
            checked={selected === p.id}
            onChange={() => setSelected(p.id)}
            className="flex-shrink-0 m-0"
            style={{ width: "1.25rem", height: "1.25rem", boxShadow: "none" }}
          />
          <div className="flex flex-col flex-1 gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <div>{p.title}</div>
              <div className="flex">
                {remaining && remaining > 0 ? (
                  <div className="small text-success">Available</div>
                ) : (
                  <div className="text-red-500 small">Sold Out</div>
                )}
                <div className="mx-2">|</div>
                <div className="small text-muted">{timeLabel || "-"}</div>
              </div>
            </div>
            <div className="small fw-bold text-nowrap">
              {formatMoney(p.priceCents, p.currency)}
              {soldOut && (
                <div>
                  <Badge bg="danger">Full</Badge>
                </div>
              )}
            </div>
          </div>
        </label>
      );
    };

    return (
      <Modal
        show={workshopModalOpen}
        onHide={() => setWorkshopModalOpen(false)}
        size="lg"
        centered
        className="ticket-modal"
      >
        <Modal.Body style={{ background: "#ebebeb" }}>
          <div className="flex flex-col gap-4 text-sm md:p-10">
            <div>
              <h3>Select Workshop</h3>
              <p className="m-0">
                Select one workshop per session. You may join the morning
                session, the afternoon session, or both.
              </p>
            </div>
            {error && <div className="alert alert-danger">{error}</div>}

            <div className="p-3 bg-white rounded md:py-4 md:px-6">
              <h5 className="mb-2">Morning sessions</h5>
              {access?.workshopAm ? (
                <div className="text-muted small">Already purchased.</div>
              ) : (
                <div className="grid grid-cols-1 gap-2 md:gap-4">
                  {(workshops?.am || []).map((p) =>
                    renderWorkshopOption(p, selectedAm, setSelectedAm),
                  )}
                </div>
              )}
              <br />
              {access?.workshopAm ? null : (
                <Button
                  className="theme-btn-red"
                  size="sm"
                  onClick={() => setSelectedAm(null)}
                >
                  Clear selection
                </Button>
              )}
            </div>

            <div className="p-3 bg-white rounded md:py-4 md:px-6">
              <h5 className="mb-2">Afternoon sessions</h5>
              {access?.workshopPm ? (
                <div className="text-muted small">Already purchased.</div>
              ) : (
                <div className="grid grid-cols-1 gap-2 md:gap-4">
                  {(workshops?.pm || []).map((p) =>
                    renderWorkshopOption(p, selectedPm, setSelectedPm),
                  )}
                </div>
              )}
              <br />
              {access?.workshopPm ? null : (
                <Button
                  className="theme-btn-red"
                  size="sm"
                  onClick={() => setSelectedPm(null)}
                >
                  Clear selection
                </Button>
              )}
            </div>
            <div className="flex justify-center gap-2">
              <Button
                className="theme-btn-light"
                onClick={() => setWorkshopModalOpen(false)}
              >
                Back
              </Button>
              <Button className="theme-btn" onClick={add}>
                Add
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <div className="container my-4" id="ticketSelection">
      {submitError && <div className="alert alert-danger">{submitError}</div>}

      <div className="text-sm row g-4">
        <div className="col-lg-7">
          <PhysicalCard />
          {!isExhibitor && <PhysicalWithHotelCard />}
          {!isExhibitor && <WorkshopCard />}
          {!isExhibitor && (
            <FreeAddOnCard
              title="Welcome Reception | 28th August 2026 (Friday)"
              product={receptionProduct}
              accessFlag="reception"
              cartKey="receptionProductId"
              disabled={!receptionProduct}
            />
          )}
          {/*<FreeAddOnCard
            title="Congress Gala Dinner | 29th August 2026 (Saturday)"
            product={galaProduct}
            accessFlag="gala"
            cartKey="galaProductId"
            disabled={!galaProduct}
          />*/}
          {!isExhibitor && (hasAccompanyingEvents || accompanyingPersons.length > 0) && (
            <AccompanyingPersonCard
              currency={currency}
              events={accompanyingEvents}
              maxCount={maxAccompanying}
              existingCount={existingAccompanying}
              canAdd={hasPhysicalAccess && hasAccompanyingEvents}
              persons={accompanyingPersons}
              onOpenModal={() => openAccompanyingModal(null)}
              onEditPerson={openAccompanyingModal}
              onRemovePerson={removeAccompanyingPerson}
            />
          )}
        </div>

        <div className="col-lg-5">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <Card className="shadow-sm">
              <Card.Body>
                <div className="mb-3 text-base">Order Summary</div>

                {cartItems.length === 0 ? (
                  <div className="flex justify-between gap-2 p-2 border-b border-dotted">
                    No items yet.
                  </div>
                ) : (
                  <div className="d-flex flex-column">
                    {regularItems.map((item) => (
                      <div
                        key={item.key}
                        className="flex justify-between gap-2 p-2 border-b border-dotted"
                      >
                        <div className="flex-grow-1">
                          <div className="text-muted">{item.title}</div>
                          {item.subtitle && (
                            <div className="text-muted">{item.subtitle}</div>
                          )}
                        </div>
                        <div className="text-end">
                          <div className="fw-bold text-nowrap">
                            {item.amountCents > 0
                              ? formatMoney(item.amountCents, currency)
                              : "Free"}
                          </div>
                        </div>
                      </div>
                    ))}
                    {accompanyingGroups.map((group) => (
                      <div
                        key={group.key}
                        className="p-2 border-b border-dotted"
                      >
                        <div className="fw-bold">{group.label}</div>
                        {group.items.map((item) => (
                          <div
                            key={item.key}
                            className="flex justify-between gap-2 pt-1"
                          >
                            <div className="text-muted">{item.title}</div>
                            <div className="fw-bold text-nowrap">
                              {item.amountCents > 0
                                ? formatMoney(item.amountCents, currency)
                                : "Free"}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                <div className="py-4 d-flex justify-content-between align-items-center">
                  <div className="fw-bold">Total</div>
                  <div className="fw-bold fs-5">
                    {formatMoney(cartTotalCents, currency)}
                  </div>
                </div>

                <Button
                  className="theme-btn w-100"
                  disabled={cartItems.length === 0 || submitting}
                  onClick={createOrder}
                >
                  {submitting ? "Processing..." : "Continue to Billing"}
                </Button>
                {submitError && (
                  <div className="mt-3 mb-0 alert alert-danger">
                    {submitError}
                  </div>
                )}
              </Card.Body>
            </Card>

            {showGrantCard && (
              <Card className="mt-4 shadow-sm">
                <Card.Body>
                  <div className="mb-3">
                    Register today to enjoy the RM400 grant for Master Trainee,
                    Medical Officer and House Officer
                  </div>
                  <div className="fw-bold">
                    Exclusive Rate Period: Until 7th August 2026
                  </div>
                  <div className="fw-bold">Coupon Code: {grantCouponCode}</div>
                  <div className="mt-2 text-muted small">
                    *The coupon code can be used after clicking on ‘Continue to
                    Billing’
                  </div>
                </Card.Body>
              </Card>
            )}
          </div>
        </div>
      </div>

      <HotelModal />
      <WorkshopModal />
      {hasAccompanyingEvents && (
        <AccompanyingPersonModal
          show={accompanyingModalOpen}
          onHide={closeAccompanyingModal}
          currency={currency}
          events={accompanyingEvents}
          registrationOptions={registrationOptions}
          defaultCountry={user?.country}
          onSavePerson={saveAccompanyingPerson}
          initialPerson={editingAccompanyingPerson}
        />
      )}
    </div>
  );
};

export default TicketSelection;
